import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { api, SESSION_EXPIRED, tokens } from "./api.js";
import type { LoginResponse, Role, SessionUser } from "./types.js";

const USER_KEY = "fanni.console.user";

interface AuthValue {
  user: SessionUser | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Role gates in the UI mirror the API's middleware. The API is still the authority. */
  can: (...roles: Role[]) => boolean;
}

const Ctx = createContext<AuthValue | null>(null);

function storedUser(): SessionUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SessionUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(() => (tokens.access() ? storedUser() : null));

  const logout = useCallback(() => {
    tokens.clear();
    localStorage.removeItem(USER_KEY);
    setUser(null);
  }, []);

  // The refresh failed, so the session is genuinely over rather than merely stale.
  useEffect(() => {
    const onExpired = () => logout();
    window.addEventListener(SESSION_EXPIRED, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED, onExpired);
  }, [logout]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api<LoginResponse>("/auth/office/login", {
      method: "POST",
      body: { email, password },
    });
    tokens.set(res.access_token, res.refresh_token);
    localStorage.setItem(USER_KEY, JSON.stringify(res.user));
    setUser(res.user);
  }, []);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      login,
      logout,
      can: (...roles: Role[]) => (user ? roles.includes(user.role) : false),
    }),
    [user, login, logout],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}

export const ROLE_AR: Record<Role, string> = {
  owner: "المالك",
  manager: "مدير",
  dispatcher: "موزّع",
  accountant: "محاسب",
  technician: "فني",
};

/** Transcribed from the API's route middleware. Kept beside it so drift is one diff. */
export const ACCESS = {
  /** jobs, catalog, dashboard — `requireOffice` */
  office: ["owner", "manager", "dispatcher", "accountant"] as Role[],
  /** /conflicts */
  conflicts: ["owner", "manager", "dispatcher"] as Role[],
  /** /money */
  money: ["owner", "manager", "accountant"] as Role[],
  /** /users */
  users: ["owner", "manager"] as Role[],
};
